const DB_NAME = 'eagle-terrain-projects';
const DB_VERSION = 2;
const LEGACY_FILE_STORE = 'files';
const PROJECT_STORE = 'projects';
const PROJECT_FILE_STORE = 'project-files';

function db() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(LEGACY_FILE_STORE)) database.createObjectStore(LEGACY_FILE_STORE, { keyPath: 'name' });
      if (!database.objectStoreNames.contains(PROJECT_STORE)) {
        const projects = database.createObjectStore(PROJECT_STORE, { keyPath: 'id' });
        projects.createIndex('updatedAt', 'updatedAt');
      }
      if (!database.objectStoreNames.contains(PROJECT_FILE_STORE)) {
        const files = database.createObjectStore(PROJECT_FILE_STORE, { keyPath: 'key' });
        files.createIndex('projectId', 'projectId');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionComplete(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error('The storage transaction was cancelled.'));
  });
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function createProject({ name, storageMode = 'local', source = 'device' } = {}) {
  const now = Date.now();
  const project = {
    id: crypto.randomUUID(),
    name: String(name || 'Untitled Project').trim() || 'Untitled Project',
    storageMode,
    source,
    crs: null,
    crsMode: 'automatic',
    fileCount: 0,
    createdAt: now,
    updatedAt: now,
  };
  const database = await db();
  const transaction = database.transaction(PROJECT_STORE, 'readwrite');
  transaction.objectStore(PROJECT_STORE).put(project);
  await transactionComplete(transaction);
  database.close();
  return project;
}

export async function saveProject(project) {
  if (!project?.id) throw new Error('Project ID is required.');
  const record = { ...project, updatedAt: project.updatedAt || Date.now() };
  const database = await db();
  const transaction = database.transaction(PROJECT_STORE, 'readwrite');
  transaction.objectStore(PROJECT_STORE).put(record);
  await transactionComplete(transaction);
  database.close();
  return record;
}

export async function getProject(projectId) {
  const database = await db();
  const project = await requestResult(database.transaction(PROJECT_STORE).objectStore(PROJECT_STORE).get(projectId));
  database.close();
  return project || null;
}

export async function listProjects() {
  const database = await db();
  const projects = await requestResult(database.transaction(PROJECT_STORE).objectStore(PROJECT_STORE).getAll());
  database.close();
  return projects.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export async function cacheFile(file, projectId) {
  const database = await db();
  const storeName = projectId ? PROJECT_FILE_STORE : LEGACY_FILE_STORE;
  const transaction = database.transaction(storeName, 'readwrite');
  const row = { name: file.name, type: file.type, lastModified: file.lastModified, blob: file, cachedAt: Date.now() };
  if (projectId) Object.assign(row, { key: `${projectId}:${file.name}`, projectId });
  transaction.objectStore(storeName).put(row);
  await transactionComplete(transaction);
  database.close();
}

export async function restoreFiles(projectId) {
  const database = await db();
  let rows;
  if (projectId) {
    const index = database.transaction(PROJECT_FILE_STORE).objectStore(PROJECT_FILE_STORE).index('projectId');
    rows = await requestResult(index.getAll(IDBKeyRange.only(projectId)));
  } else {
    rows = await requestResult(database.transaction(LEGACY_FILE_STORE).objectStore(LEGACY_FILE_STORE).getAll());
  }
  database.close();
  return rows.sort((a, b) => a.cachedAt - b.cachedAt).map(row => new File([row.blob], row.name, { type: row.type, lastModified: row.lastModified }));
}

async function deleteFilesForProject(database, projectId) {
  const transaction = database.transaction(PROJECT_FILE_STORE, 'readwrite');
  const completed = transactionComplete(transaction);
  const index = transaction.objectStore(PROJECT_FILE_STORE).index('projectId');
  await new Promise((resolve, reject) => {
    const cursorRequest = index.openKeyCursor(IDBKeyRange.only(projectId));
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) return resolve();
      transaction.objectStore(PROJECT_FILE_STORE).delete(cursor.primaryKey);
      cursor.continue();
    };
    cursorRequest.onerror = () => reject(cursorRequest.error);
  });
  await completed;
}
export async function deleteCachedFile(
  projectId,
  fileName
) {
  if (!projectId || !fileName) {
    return false;
  }

  const database = await db();

  const transaction =
    database.transaction(
      PROJECT_FILE_STORE,
      'readwrite'
    );

  transaction
    .objectStore(PROJECT_FILE_STORE)
    .delete(
      `${projectId}:${fileName}`
    );

  await transactionComplete(
    transaction
  );

  database.close();

  return true;
}
export async function clearCachedFiles(projectId) {
  const database = await db();
  if (projectId) await deleteFilesForProject(database, projectId);
  else {
    const transaction = database.transaction(LEGACY_FILE_STORE, 'readwrite');
    transaction.objectStore(LEGACY_FILE_STORE).clear();
    await transactionComplete(transaction);
  }
  database.close();
}

export async function deleteProject(projectId) {
  const database = await db();
  await deleteFilesForProject(database, projectId);
  const transaction = database.transaction(PROJECT_STORE, 'readwrite');
  transaction.objectStore(PROJECT_STORE).delete(projectId);
  await transactionComplete(transaction);
  database.close();
}
